import React, { useState, useEffect } from 'react';
import { Plus, X, UserCheck, UserX } from 'lucide-react';
import { TeamMember, visionService } from '../../services/database/vision';
import { toast } from 'sonner';

interface TeamManagementProps {
  onClose: () => void;
  onSelect?: (selectedMembers: string[]) => void;
  selectedMembers?: string[];
  selectionMode?: boolean;
}

export const TeamManagement: React.FC<TeamManagementProps> = ({
  onClose,
  onSelect,
  selectedMembers = [],
  selectionMode = false
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: ''
  });
  const [selected, setSelected] = useState<string[]>(selectedMembers);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const data = await visionService.getTeamMembers();
      setMembers(data);
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
      toast.error('Erro ao carregar membros');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await visionService.addTeamMember(formData);
      setFormData({ name: '', role: '' });
      setShowForm(false);
      loadMembers();
      toast.success('Membro adicionado com sucesso!');
    } catch (error) {
      console.error('Erro ao adicionar membro:', error);
      toast.error('Erro ao adicionar membro');
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await visionService.deactivateTeamMember(id);
      loadMembers();
      toast.success('Membro desativado com sucesso!');
    } catch (error) {
      console.error('Erro ao desativar membro:', error);
      toast.error('Erro ao desativar membro');
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelected(prev => 
      prev.includes(id)
        ? prev.filter(m => m !== id)
        : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-[#111]/90 rounded-2xl p-8 w-full max-w-2xl border border-white/[0.08]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            {selectionMode ? 'Selecionar Membros' : 'Gerenciar Equipe'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/[0.08] rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        {!selectionMode && (
          <button
            onClick={() => setShowForm(true)}
            className="mb-6 h-12 px-6 bg-blue-500 hover:bg-blue-600 rounded-xl text-white flex items-center gap-2"
          >
            <Plus size={20} />
            Novo Membro
          </button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Nome
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full h-12 bg-white/[0.08] rounded-xl px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">
                Função
              </label>
              <input
                type="text"
                value={formData.role}
                onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full h-12 bg-white/[0.08] rounded-xl px-4 text-white focus:outline-none focus:ring-2 focus:ring-white/[0.16]"
                required
              />
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 h-12 bg-white/[0.08] hover:bg-white/[0.12] rounded-xl text-white"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 h-12 bg-blue-500 hover:bg-blue-600 rounded-xl text-white"
              >
                Adicionar
              </button>
            </div>
          </form>
        )}

        <div className="space-y-4">
          {members.map(member => (
            <div
              key={member.id}
              className={`p-4 rounded-xl border ${
                selectionMode
                  ? selected.includes(member.id)
                    ? 'bg-blue-500/20 border-blue-500'
                    : 'bg-white/[0.08] border-white/[0.08]'
                  : 'bg-white/[0.08] border-white/[0.08]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">{member.name}</h3>
                  <p className="text-sm text-gray-400">{member.role}</p>
                </div>
                {selectionMode ? (
                  <button
                    onClick={() => handleToggleSelect(member.id)}
                    className={`p-2 rounded-lg ${
                      selected.includes(member.id)
                        ? 'bg-blue-500 text-white'
                        : 'bg-white/[0.08] hover:bg-white/[0.12]'
                    }`}
                  >
                    <UserCheck size={20} />
                  </button>
                ) : (
                  <button
                    onClick={() => handleDeactivate(member.id)}
                    className="p-2 hover:bg-white/[0.08] rounded-lg text-red-500"
                  >
                    <UserX size={20} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {selectionMode && (
          <div className="mt-6 flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 h-12 bg-white/[0.08] hover:bg-white/[0.12] rounded-xl text-white"
            >
              Cancelar
            </button>
            <button
              onClick={() => onSelect?.(selected)}
              className="flex-1 h-12 bg-blue-500 hover:bg-blue-600 rounded-xl text-white"
            >
              Confirmar Seleção
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
